/**
 * Which ONE page-wide status strip the app shell shows — ROUTE_INVENTORY
 * Part 1, G6–G9.
 *
 * Only one at a time, by priority, highest first:
 *
 *   1. **Session expired** — nothing else works until the user signs in again,
 *      so every other message is moot.
 *   2. **Offline** — nothing reaches the server; a backend or billing problem
 *      cannot even be known.
 *   3. **Service issues** — the backend is failing; the app half-works.
 *   4. **Billing** — recoverable, and the app still works. Action required
 *      (an SCA challenge that will fail the renewal if ignored) outranks past
 *      due (Stripe is still retrying).
 *
 * A DISMISSED banner is out of the running, so dismissing the top one reveals
 * the next rather than hiding them all. Each banner keeps its own dismissal
 * rule (service issues holds for the outage, session expired until the next
 * failed request); this function only reads the result. Offline cannot be
 * dismissed — it clears when the connection returns.
 *
 * Pure so the whole table can be tested without mounting four subscriptions:
 * `src/__tests__/unit/lib/banners/pickShellBanner.test.ts`.
 */
export type ShellBannerId =
  | 'session-expired'
  | 'offline'
  | 'service-issues'
  | 'billing-action-required'
  | 'billing-past-due';

export interface ShellBannerStates {
  sessionExpired: { raised: boolean; dismissed: boolean };
  offline: { raised: boolean };
  serviceIssues: { raised: boolean; dismissed: boolean };
  billing: {
    kind: 'action-required' | 'past-due' | null;
    dismissed: boolean;
  };
}

export function pickShellBanner({
  sessionExpired,
  offline,
  serviceIssues,
  billing,
}: ShellBannerStates): ShellBannerId | null {
  if (sessionExpired.raised && !sessionExpired.dismissed)
    return 'session-expired';
  if (offline.raised) return 'offline';
  if (serviceIssues.raised && !serviceIssues.dismissed) return 'service-issues';
  if (billing.kind && !billing.dismissed) return `billing-${billing.kind}`;
  return null;
}
